import { useMemo, useState } from 'react';
import { Button, CircularProgress, Divider, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';

// Friendly names for the DocuSeal submission groups we generate.
const GROUP_LABELS = {
  household: 'Household Package',
  'cash-tip': 'Cash Tip Income Certification',
  'per-adult': 'Per-Adult Certifications',
  'lia-certification': 'Live-In Aide Certification',
  'lia-questionnaire': 'Live-In Aide Questionnaire',
  'live-in-aide': 'Live-In Aide Package',
};
const groupLabel = g => GROUP_LABELS[g] ?? (g ? g.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Forms');

// Friendly names for the individual forms shown as the "includes" line.
const FORM_LABELS = {
  'tdhca-tic': 'Tenant Income Certification',
  'asset-certification': 'Asset Certification',
  'taa-supplemental': 'TAA Supplemental',
  'tdhca-special-needs': 'Special Needs',
  'student-eligibility': 'Student Eligibility',
  'tenant-selection-criteria': 'Tenant Selection Criteria',
  'tenant-release-consent': 'Release & Consent',
  'trr-guide-ack': "Renter's Rights Guide",
  'cash-tip-income-cert': 'Cash Tip Income Cert',
  'unborn-child-self-cert': 'Unborn Child Self-Cert',
  'lia-certification': 'Live-In Aide Certification',
  'lia-questionnaire': 'Live-In Aide Questionnaire',
  'cert-of-zero-income': 'Certification of Zero Income',
  'non-employed-cert': 'Non-Employed Certification',
};
const formLabel = k => FORM_LABELS[k] ?? k;

// Overall (package) status → chip tone + label.
const PKG = {
  completed: { tone: 'success', label: 'Completed' },
  opened: { tone: 'info', label: 'In progress' },
  sent: { tone: 'warning', label: 'Awaiting signatures' },
  declined: { tone: 'error', label: 'Declined' },
  pending: { tone: 'neutral', label: 'Not sent' },
};
// Per-signer status → chip tone + label.
const SIGNER = {
  completed: { tone: 'success', label: 'Signed' },
  opened: { tone: 'info', label: 'Opened' },
  declined: { tone: 'error', label: 'Declined' },
  pending: { tone: 'neutral', label: 'Not started' },
};

const fmtDate = d => (d ? moment(d).format('MMM D, YYYY h:mm A') : '—');

// Link-only site-team counter-signers — shown apart from household applicants so
// staff can tell who's internal (them) vs the resident.
const SITE_TEAM_ROLES = new Set(["Owner's Representative", 'Management']);
const isSiteTeam = role => SITE_TEAM_ROLES.has(role);
// Household applicants first, site-team signers last.
const orderSigners = signers => [...signers].sort((a, b) => Number(isSiteTeam(a.role)) - Number(isSiteTeam(b.role)));

export default function SigningStatusPanel({ lease }) {
  const [openingDocUrl, setOpeningDocUrl] = useState(null);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [`admin/lease/${lease.id}/signing-status`],
    queryFn: () => api.get(`/v1/admin/lease/${lease.id}/signing-status`),
    enabled: Boolean(lease?.id),
    // Keep the tab fresh while any package is still in flight (webhook/cron drive
    // the DB; this just re-reads it).
    refetchInterval: query => {
      const pkgs = query.state.data?.data?.packages ?? [];
      return pkgs.some(p => p.status !== 'completed' && p.status !== 'declined') ? 20000 : false;
    },
  });

  const packages = useMemo(() => data?.data?.packages ?? [], [data]);
  const copySigningLink = url => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Signing link copied'),
      () => toast.error('Could not copy the signing link')
    );
  };

  const resendMutation = useMutation({
    mutationFn: submitterId => api.post(`/v1/admin/lease/${lease.id}/submitters/${submitterId}/resend`),
    onSuccess: () => {
      toast.success('Signing email resent');
      refetch();
    },
    onError: err => {
      toast.error(err?.response?.data?.message ?? 'Could not resend the signing email.');
    },
  });
  const openDocumentUrl = async url => {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    setOpeningDocUrl(url);
    try {
      const response = await api.get(url);
      const freshUrl = response?.data?.url;
      if (!freshUrl) throw new Error('Missing document URL');
      if (popup) {
        popup.location.href = freshUrl;
      } else {
        window.open(freshUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      if (popup) popup.close();
      toast.error(err?.response?.data?.message ?? 'Could not open signed PDF. Please refresh and try again.');
    } finally {
      setOpeningDocUrl(null);
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (error) {
    return <Typography sx={{ color: 'error.main' }}>{error?.response?.data?.message ?? 'Could not load signing status. Try again later.'}</Typography>;
  }
  if (packages.length === 0) {
    return (
      <Panel title="Signing Status">
        <Typography variant="body2" color="text.secondary">
          No signing package has been generated for this lease yet. Generate one from the <b>Income Verification</b> tab, then track each signer&rsquo;s
          progress here.
        </Typography>
      </Panel>
    );
  }

  return (
    <Stack gap={2.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          Live signing progress for each generated package. Updates automatically as signers open and sign.
        </Typography>
        <Button size="small" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      {packages.map(pkg => {
        const pkgMeta = PKG[pkg.status] ?? PKG.pending;
        const signedCount = pkg.signers.filter(s => s.status === 'completed').length;
        return (
          <Panel
            key={pkg.externalSubmissionId ?? pkg.formKeys.join(',')}
            title={groupLabel(pkg.submissionGroup)}
            action={<StatusChip tone={pkgMeta.tone} label={pkgMeta.label} size="small" />}
          >
            <Stack gap={1.5}>
              <Typography variant="caption" color="text.secondary">
                Includes: {pkg.formKeys.map(formLabel).join(' · ')}
              </Typography>

              {pkg.signers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No signers on this package yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Signer</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Signed</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderSigners(pkg.signers).map((s, i) => {
                      const meta = SIGNER[s.status] ?? SIGNER.pending;
                      const site = isSiteTeam(s.role);
                      // Link-only site-team signer (no email) still needing to sign.
                      const showEmbed = s.embedSrc && s.status !== 'completed' && s.status !== 'declined';
                      const canResend = !site && s.id && s.embedSrc && s.status !== 'completed' && s.status !== 'declined';
                      const resending = resendMutation.isPending && resendMutation.variables === s.id;
                      return (
                        <TableRow key={s.id ?? `${s.role}-${i}`} sx={site ? { backgroundColor: 'rgba(44, 98, 107, 0.05)' } : undefined}>
                          <TableCell>
                            <Typography variant="body2">{s.name || '—'}</Typography>
                            {s.email && (
                              <Typography variant="caption" color="text.secondary">
                                {s.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                              {s.role}
                              {site && <StatusChip tone="info" label="Site team" size="small" dot={false} />}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <StatusChip tone={meta.tone} label={meta.label} size="small" />
                          </TableCell>
                          <TableCell>{fmtDate(s.signedAt)}</TableCell>
                          <TableCell align="right">
                            {site && showEmbed ? (
                              <Button size="small" href={s.embedSrc} target="_blank" rel="noopener noreferrer">
                                Sign
                              </Button>
                            ) : canResend ? (
                              <Stack direction="row" gap={1} justifyContent="flex-end">
                                <Button size="small" onClick={() => copySigningLink(s.embedSrc)}>
                                  Copy link
                                </Button>
                                <Button size="small" disabled={resending} onClick={() => resendMutation.mutate(s.id)}>
                                  Resend
                                </Button>
                              </Stack>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              <Typography variant="caption" color="text.secondary">
                {signedCount} of {pkg.signers.length} signed
                {pkg.completedAt ? ` · completed ${fmtDate(pkg.completedAt)}` : ''}
              </Typography>

              {(pkg.signedPdfs.length > 0 || pkg.auditLogUrl) && (
                <>
                  <Divider />
                  <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                    {pkg.signedPdfs.map(doc => (
                      <Link
                        key={doc.formKey}
                        component="button"
                        type="button"
                        variant="body2"
                        disabled={openingDocUrl === doc.url}
                        onClick={() => openDocumentUrl(doc.url)}
                      >
                        {formLabel(doc.formKey)} {openingDocUrl === doc.url ? '(opening...)' : '(signed PDF)'}
                      </Link>
                    ))}
                    {pkg.auditLogUrl && (
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        disabled={openingDocUrl === pkg.auditLogUrl}
                        onClick={() => openDocumentUrl(pkg.auditLogUrl)}
                      >
                        Audit log{openingDocUrl === pkg.auditLogUrl ? ' (opening...)' : ''}
                      </Link>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          </Panel>
        );
      })}
    </Stack>
  );
}
